import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';

import DragWrapperContainer from './DragWrapperContainer';
import TabNav from './TabNav';
import TabPane from './TabPane';
import styles from './styles';

const useStyles = createUseStyles(styles, 'Tabs');

function Tabs({
    classes: classesProp,
    overrides: overridesProp,
    className: classNameProp,
    onChange,
    onClose,
    activeKey,
    postComponent,
    containerElement,
    popoverOffsetCorrection = 0,
    popoverAlignLeft,
    position = 'top',
    tabs = [],
    editable = false,
    alwaysShowCloseTab = false,
    draggable,
    onSorting,
}) {
    const [state, setState] = useState({
        tabs: [],
        activeKey: null,
    });
    const [popoverComponent, setPopoverComponent] = useState(null);
    const [popoverStyles, setPopoverStyles] = useState({});
    const tabRef = useRef(null);
    const classes = useClasses(useStyles, classesProp);
    // Overrides
    const override = getOverrides(overridesProp, Tabs.overrides);

    useEffect(() => {
        setState({ activeKey, tabs });
    }, [activeKey, tabs]);

    // Classes
    const rootClassName = classnames(
        classes.root,
        {
            [classes.withPostComponent]: postComponent,
        },
        classNameProp,
    );

    const handleChange = useCallback(
        (key) => {
            setState((state) => ({ ...state, activeKey: key }));
            onChange && onChange(key);
        },
        [onChange],
    );

    const onMouseOut = useCallback((e) => {
        e?.stopPropagation();
        setPopoverComponent(null);
        setPopoverStyles({ display: 'none' });
    }, []);

    const onClickForPopover = useCallback((e) => {
        setPopoverComponent(null);
        setPopoverStyles({ display: 'none' });
    }, []);

    const handleClose = useCallback(
        (event, key) => {
            event.stopPropagation();

            let { activeKey, tabs } = state;
            const foundIndex = tabs.findIndex((tab) => tab.key === key);

            if (foundIndex === -1) return;

            const finalTabs = tabs.filter((tab) => tab.key !== key);

            if (activeKey === key) {
                const newIndex = foundIndex > 0 ? foundIndex - 1 : 0;
                activeKey = finalTabs[newIndex]?.key;
            }

            setState((state) => ({ ...state, activeKey, tabs: finalTabs }));
            onClose && onClose({ key, activeKey, tabs: finalTabs });
            onMouseOut();
        },
        [onClose, onMouseOut, state],
    );

    const findTab = useCallback(
        (key) => {
            const index = state.tabs.findIndex((c) => String(c.key) === String(key));
            return {
                tab: state.tabs[index],
                index,
            };
        },
        [state.tabs],
    );

    const moveTab = useCallback(
        (key, atIndex) => {
            const { tab, index } = findTab(key);
            const newTabs = [...state.tabs];
            newTabs.splice(index, 1);
            newTabs.splice(atIndex, 0, tab);
            setState((current) => ({ ...current, tabs: newTabs }));
            onSorting?.(newTabs);
        },
        [findTab, state.tabs, onSorting],
    );

    const getParentNode = useCallback((target) => {
        const popoverTrigger = target.closest('[data-popover-trigger="true"]');
        return popoverTrigger?.parentNode || null;
    }, []);

    const getAbsolutePosition = useCallback((element) => {
        const rect = element.getBoundingClientRect();

        return {
            top: rect.top,
            bottom: rect.bottom,
            left: rect.left,
            right: rect.right,
        };
    }, []);

    const onMouseOver = useCallback(
        (e, popoverContent, popoverWidth = 100) => {
            e.stopPropagation();
            const element = tabRef.current || null;
            if (!element) return;

            let parentNode = null;

            parentNode = getParentNode(e.target);
            if (!parentNode) return;

            const { left, right } = getAbsolutePosition(parentNode);

            let tabWidth = right - left;
            let offsetToTabCenter = tabWidth / 2;

            let positionToTabCenter = left + offsetToTabCenter;
            const pixelsToPopoverCenter = popoverWidth / 2;

            let popoverStyles = {
                display: 'block',
                top: element.offsetTop + element.offsetHeight,
            };

            if (containerElement) {
                const containerElementPosition = getAbsolutePosition(containerElement);
                const canFit = tabWidth > popoverWidth;
                const differenceTabAndPopover = popoverWidth - tabWidth;
                const shadowOffset = 6;

                if (
                    !canFit &&
                    left - containerElementPosition.left + popoverOffsetCorrection <
                        differenceTabAndPopover
                ) {
                    popoverStyles.left = shadowOffset;
                } else if (
                    !canFit &&
                    containerElementPosition.right + popoverOffsetCorrection - right <
                        differenceTabAndPopover
                ) {
                    popoverStyles.right = shadowOffset;
                } else {
                    positionToTabCenter =
                        positionToTabCenter -
                        containerElementPosition.left -
                        popoverOffsetCorrection;
                    popoverStyles.left = positionToTabCenter - pixelsToPopoverCenter;
                }
            } else {
                popoverStyles.left = positionToTabCenter - pixelsToPopoverCenter;
            }

            if (popoverAlignLeft) popoverStyles.left = left;

            setPopoverComponent(popoverContent);
            setPopoverStyles(popoverStyles);
        },
        [
            getParentNode,
            getAbsolutePosition,
            popoverAlignLeft,
            containerElement,
            popoverOffsetCorrection,
        ],
    );

    const tabsClassName = useMemo(() => {
        return classnames(classes.tabsContainer, {
            [classes.tabsBottom]: position === 'bottom',
        });
    }, [position, classes]);

    const contentClassName = useMemo(
        () => classnames(classes.content, classes.contentAnimated),
        [classes],
    );

    const TabsEl = useMemo(() => {
        return (
            <div className={tabsClassName}>
                <TabNav
                    tabs={state.tabs}
                    activeKey={state.activeKey}
                    position={position}
                    editable={editable}
                    alwaysShowCloseTab={alwaysShowCloseTab}
                    onTabClick={handleChange}
                    onMouseOver={onMouseOver}
                    onMouseOut={onMouseOut}
                    onClickForPopover={onClickForPopover}
                    onClose={handleClose}
                    classes={classes}
                    override={override}
                    draggable={draggable}
                    moveTab={moveTab}
                    findTab={findTab}
                />
                <div className={contentClassName}>
                    {state.tabs.map(({ key, content }) => (
                        <TabPane
                            key={key}
                            active={state.activeKey === key}
                            id={`panel-${key}`}
                            tabId={`tab-${key}`}
                            classes={classes}
                        >
                            {content}
                        </TabPane>
                    ))}
                </div>
            </div>
        );
    }, [
        tabsClassName,
        contentClassName,
        state.tabs,
        state.activeKey,
        position,
        editable,
        alwaysShowCloseTab,
        handleChange,
        onMouseOver,
        onMouseOut,
        onClickForPopover,
        handleClose,
        classes,
        override,
        draggable,
        moveTab,
        findTab,
    ]);

    const WrappedTabsEl = useMemo(() => {
        if (draggable) {
            return (
                <DndProvider backend={HTML5Backend}>
                    <DragWrapperContainer>{TabsEl}</DragWrapperContainer>
                </DndProvider>
            );
        }
        return TabsEl;
    }, [TabsEl, draggable]);

    return (
        <div className={rootClassName} {...override.root} ref={tabRef}>
            {WrappedTabsEl}
            {postComponent && <div className={classes.postComponent}>{postComponent}</div>}
            {popoverComponent && (
                <div className={classes.popover} style={popoverStyles}>
                    {popoverComponent}
                </div>
            )}
        </div>
    );
}

Tabs.overrides = ['root', 'rc-tabs', 'close'];

Tabs.propTypes = {
    /** Custom CSS class for styling the root container */
    className: PropTypes.string,
    /** Object with custom style overrides */
    overrides: PropTypes.object,
    /** Array of tab objects that define the tabs to be displayed */
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            /** Unique identifier for the tab */
            key: PropTypes.string.isRequired,
            /** Text or component to display in the tab header */
            title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
            /** Content to display when the tab is active */
            content: PropTypes.node,
            /** Content to show in a popover when hovering over the tab */
            popoverContent: PropTypes.node,
            /** Width of the popover in pixels */
            popoverWidth: PropTypes.number,
            /** When true, the tab cannot be closed (used with editable=true) */
            fixed: PropTypes.bool,
        }),
    ),
    /** Key of the currently active tab */
    activeKey: PropTypes.string,
    /** Callback triggered when a tab is clicked. Receives the tab key as parameter */
    onChange: PropTypes.func,
    /** Callback triggered when a tab is closed. Receives an object with key, activeKey and tabs */
    onClose: PropTypes.func,
    /** Position of the tabs relative to content ('top', 'bottom') */
    position: PropTypes.oneOf(['top', 'bottom']),
    /** Component to display after the tabs */
    postComponent: PropTypes.node,
    /** When true, tabs can be closed with an X icon */
    editable: PropTypes.bool,
    /** When true, tabs always show close button even if there's only one tab */
    alwaysShowCloseTab: PropTypes.bool,
    /** Reference to the container element for positioning popovers within a specific container */
    containerElement: PropTypes.any,
    /** Pixel offset correction for popover positioning */
    popoverOffsetCorrection: PropTypes.number,
    /** When true, aligns popover to the left edge of the tab instead of centering it */
    popoverAlignLeft: PropTypes.bool,
    /** When true, enables drag and drop reordering of tabs */
    draggable: PropTypes.bool,
    /** Callback triggered when tabs are reordered. Receives the new tabs array */
    onSorting: PropTypes.func,
};

export default memo(Tabs);
